/**
 * Scryfall data layer — now backed by IndexedDB instead of localStorage.
 *
 * Two expiry strategies:
 *   - Images (image_uris): never expire — card art never changes
 *   - Prices + metadata: expire after SCRYFALL_CACHE_TTL_MS
 *
 * In-memory map is rebuilt from IDB on first access per session.
 */

import {
  getAllScryfallEntries, putScryfallEntries,
  clearScryfallStore, setMeta, getMeta
} from './db'
import {
  cardPrintRowToSfEntry,
  fetchCardPrintsByScryfallIds,
  fetchCardPrintsBySetCollector,
  ensureCardPrints,
} from './cardPrints'
import { perfSpan } from './perf'

const BATCH_SIZE = 75
// Scryfall asks for no more than ~10 requests/second. The semaphore spaces
// request *starts* by this gap, so with SF_CONCURRENCY = 2 the app's ceiling is
// 1000/DELAY_MS: 80 ms put it at 12.5/s, above what they publish. 100 ms lands
// on 10/s.
const DELAY_MS   = 100
const SF_CONCURRENCY = 2
// Metadata/price freshness window. Shared prices refresh on their own 10-minute
// cycle in sharedCardPrices.js, so this only paces Scryfall metadata refetches.
export const SCRYFALL_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const SF_API_ORIGIN = 'https://api.scryfall.com'
const SCRYFALL_METADATA_UPDATED_AT_KEY = 'scryfall_metadata_updated_at'
const LEGACY_SCRYFALL_PRICES_UPDATED_AT_KEY = 'scryfall_prices_updated_at'

async function getMetadataUpdatedAt() {
  const current = await getMeta(SCRYFALL_METADATA_UPDATED_AT_KEY)
  if (current != null) return current

  const legacy = await getMeta(LEGACY_SCRYFALL_PRICES_UPDATED_AT_KEY)
  if (legacy != null) {
    await setMeta(SCRYFALL_METADATA_UPDATED_AT_KEY, legacy)
    return legacy
  }
  return null
}

// Scryfall's API sends `Access-Control-Allow-Origin: *`, so the browser fetches
// it directly in every environment — no dev proxy. (The old `/api/scryfall`
// Vite proxy was removed alongside the EDHREC/import proxies; rewriting to it
// here silently 404'd to the SPA fallback and broke all dev Scryfall search.)
export function sfUrl(url) {
  return url.startsWith('/') ? `${SF_API_ORIGIN}${url}` : url
}

// ── Shared Scryfall fetch helper ───────────────────────────────────────────────
// Enforces 100ms minimum between requests and adds required Accept header.
// User-Agent cannot be set from browser JS (forbidden header) — browser sends its own.
const SF_HEADERS = { 'Accept': 'application/json' }
let _lastSfCall = 0
let _activeSfCalls = 0
const _sfWaiters = []

function getRetryDelayMs(res) {
  const retryAfter = res.headers?.get?.('Retry-After')
  if (!retryAfter) return 1000
  const seconds = Number.parseFloat(retryAfter)
  if (Number.isFinite(seconds)) return Math.max(1000, seconds * 1000)
  const dateMs = Date.parse(retryAfter)
  return Number.isFinite(dateMs) ? Math.max(1000, dateMs - Date.now()) : 1000
}

// Semaphore: up to SF_CONCURRENCY in-flight, with min-gap between request starts.
async function acquireSfSlot(minDelayMs) {
  if (_activeSfCalls >= SF_CONCURRENCY) {
    await new Promise(r => _sfWaiters.push(r))
  }
  _activeSfCalls++
  const wait = Math.max(0, minDelayMs - (Date.now() - _lastSfCall))
  if (wait) await new Promise(r => setTimeout(r, wait))
  _lastSfCall = Date.now()
}

function releaseSfSlot() {
  _activeSfCalls--
  const next = _sfWaiters.shift()
  if (next) next()
}

async function runScryfallRequest(fn, { minDelayMs = DELAY_MS, retries = 3 } = {}) {
  await acquireSfSlot(minDelayMs)
  try {
    let lastErr = null
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const res = await fn()
        // Retry on rate-limit and transient 5xx; everything else returns.
        if (res.status === 429) {
          if (attempt === retries) return res
          await new Promise(r => setTimeout(r, getRetryDelayMs(res)))
          _lastSfCall = Date.now()
          continue
        }
        if (res.status >= 500 && res.status < 600) {
          if (attempt === retries) return res
          const backoff = Math.min(8000, 500 * 2 ** attempt)
          await new Promise(r => setTimeout(r, backoff))
          _lastSfCall = Date.now()
          continue
        }
        return res
      } catch (err) {
        // Network/abort errors — retry with backoff.
        lastErr = err
        if (attempt === retries) throw err
        const backoff = Math.min(8000, 500 * 2 ** attempt)
        await new Promise(r => setTimeout(r, backoff))
        _lastSfCall = Date.now()
      }
    }
    if (lastErr) throw lastErr
  } finally {
    releaseSfSlot()
  }
}

/**
 * Like `sfGet`, but says *why* there is no data.
 *
 * `sfGet` answers null for two different things, and a caller that reads an
 * empty result as a fact needs them apart: `/cards/search` replies **404** when
 * nothing matches, which is a real answer ("no such cards"), while a 429, a 5xx
 * or a dropped connection is no answer at all. Conflating them turns a
 * rate-limited request into a confident "zero results".
 *
 * Returns { ok, status, json? }. `status: 0` means the request never completed.
 */
export async function sfGetOrStatus(url, opts = {}) {
  try {
    const fetchOpts = { headers: SF_HEADERS }
    if (opts.noCache) fetchOpts.cache = 'no-store'
    // `retries` is exposed because retrying is not always right. A rate-limited
    // Scryfall answers 429 *without* CORS headers, so the browser reports it as
    // a network error and the retry loop treats it as transient — turning one
    // rate-limited request into four, each one refreshing the cooldown. Callers
    // doing optional enrichment should pass 0.
    const res = await runScryfallRequest(
      () => fetch(sfUrl(url), fetchOpts),
      opts.retries != null ? { retries: opts.retries } : undefined,
    )
    if (!res) return { ok: false, status: 0 }
    if (!res.ok) return { ok: false, status: res.status }
    return { ok: true, status: res.status, json: await res.json() }
  } catch (err) {
    console.warn('[SF] sfGet failed', url, err?.message || err)
    return { ok: false, status: 0 }
  }
}

export async function sfGet(url, opts = {}) {
  const result = await sfGetOrStatus(url, opts)
  return result.ok ? result.json : null
}

// Clear old localStorage keys
;['arcanevault_sfcache','arcanevault_sfcache_v2',
  'arcanevault_prices_v1','arcanevault_prices_v2','arcanevault_prices_v3',
  'arcanevault_images_v1'].forEach(k => { try { localStorage.removeItem(k) } catch {} })

// ── In-memory map ─────────────────────────────────────────────────────────────
// Rebuilt from IDB on first enrichCards call, survives navigation
let _sfMap = null  // { 'set-col': { prices, type_line, image_uris, ... } }

function buildMapFromEntries(entries) {
  const map = {}
  for (const e of entries) map[e.key] = e
  return map
}

// ── Public cache management ───────────────────────────────────────────────────

// Clears fetched metadata while keeping cached images.
export async function clearScryfallCache() {
  _sfMap = null
  const entries = await getAllScryfallEntries()
  // Strip metadata fields that can be re-fetched, keep image fields.
  const stripped = entries.map(e => ({
    ...e,
    prices: null,
    prices_updated_at: null,
    rarity: null, type_line: null, set_name: null,
    color_identity: null, cmc: null, legalities: null,
    artist: null, oracle_text: null, power: null, toughness: null,
  }))
  await putScryfallEntries(stripped)
  await setMeta(SCRYFALL_METADATA_UPDATED_AT_KEY, null)
  await setMeta(LEGACY_SCRYFALL_PRICES_UPDATED_AT_KEY, null)
  console.log('[SF] metadata cache cleared (images kept)')
}

// Clears everything
export async function clearAllScryfallCache() {
  _sfMap = null
  await clearScryfallStore()
  console.log('[SF] all cache cleared')
}

export async function getCacheAge() {
  const ts = await getMetadataUpdatedAt()
  return ts ? Date.now() - ts : null
}

// Synchronous best-effort: return in-memory map if available
// Used to pre-populate sfMap state before async load completes
export function getMemoryMap() {
  return _sfMap
}

// ── Worker-prefetched price rows (Phase 2e) ───────────────────────────────────
// The hydrate worker reads today's+yesterday's card_prices rows off-thread.
// Consumed once by the first price overlay, then cleared so later overlays
// (after price writes) read fresh from IDB.
let _prefetchedPriceRows = null

function stashPrefetchedPriceRows(rows, dates) {
  _prefetchedPriceRows = { rows, dates, at: Date.now() }
}

// Returns the prefetched rows if they cover exactly `snapshotDates` and are
// recent (the worker ran this page load), else null. Single-use.
export function consumePrefetchedPriceRows(snapshotDates) {
  const p = _prefetchedPriceRows
  if (!p) return null
  _prefetchedPriceRows = null
  if (Date.now() - p.at > 30000) return null
  const want = [...snapshotDates].sort().join('|')
  const have = [...(p.dates || [])].sort().join('|')
  return want === have ? p.rows : null
}

// ── Async cache loader ────────────────────────────────────────────────────────
// Load all entries from IDB into memory. Returns map or null if empty.
// Off-main-thread hydration (Phase 2b). Resolves null when workers are
// unavailable or anything goes wrong — callers fall back to the direct path.
function hydrateViaWorker() {
  if (typeof Worker === 'undefined') return Promise.resolve(null)
  return new Promise(resolve => {
    let worker
    try {
      worker = new Worker(new URL('./hydrateWorker.js', import.meta.url), { type: 'module' })
    } catch {
      return resolve(null)
    }
    const finish = (result) => {
      clearTimeout(timer)
      worker.terminate()
      resolve(result)
    }
    const timer = setTimeout(() => finish(null), 10000)
    worker.onmessage = e => finish(e.data?.ok && e.data.count > 0 ? e.data : null)
    worker.onerror = () => finish(null)
    worker.postMessage('hydrate')
  })
}

export async function loadCacheFromIDB() {
  if (_sfMap) return _sfMap

  const endHydrate = perfSpan('idb-hydrate')
  const viaWorker = await hydrateViaWorker()
  if (viaWorker) {
    const updatedAt = await getMetadataUpdatedAt()
    const expired = !updatedAt || (Date.now() - updatedAt > SCRYFALL_CACHE_TTL_MS)
    console.log(`[SF IDB] loaded ${viaWorker.count} cards (worker) — metadata ${expired ? 'EXPIRED' : 'fresh'}`)
    _sfMap = viaWorker.map
    // The worker also pre-read today's+yesterday's price rows off-thread; stash
    // them so the next price overlay skips its own main-thread IDB read.
    if (viaWorker.priceRows) {
      stashPrefetchedPriceRows(viaWorker.priceRows, viaWorker.priceDates)
    }
    endHydrate()
    return { map: _sfMap, pricesExpired: expired }
  }

  // Direct path: workers unavailable (tests, very old WebViews) or empty DB.
  const endRead = perfSpan('idb-hydrate:read')
  const entries = await getAllScryfallEntries()
  endRead()
  if (!entries.length) {
    console.log('[SF IDB] empty')
    return null
  }

  // Check metadata freshness
  const updatedAt = await getMetadataUpdatedAt()
  const expired = !updatedAt || (Date.now() - updatedAt > SCRYFALL_CACHE_TTL_MS)

  console.log(`[SF IDB] loaded ${entries.length} cards — metadata ${expired ? 'EXPIRED' : 'fresh'}`)

  const endBuild = perfSpan('idb-hydrate:build-map')
  _sfMap = buildMapFromEntries(entries)
  endBuild()
  return { map: _sfMap, pricesExpired: expired }
}

export async function getInstantCache() {
  // If already in memory, return directly — loadCacheFromIDB returns the raw map
  // (not wrapped) in the `if (_sfMap) return _sfMap` fast-path, which breaks
  // the result.map accessor. Bypass it entirely when in memory.
  if (_sfMap) return _sfMap
  const result = await loadCacheFromIDB()
  if (!result) return null
  return result.map
}

// ── Fetching ──────────────────────────────────────────────────────────────────

// Returns { data, ok } so callers can distinguish "card not found in Scryfall"
// (ok=true, data=[]) from "request failed" (ok=false). Previously these were
// indistinguishable, which masked network failures as successful empty fetches.
export async function fetchScryfallBatch(identifiers) {
  try {
    const res = await runScryfallRequest(() => fetch(sfUrl(`${SF_API_ORIGIN}/cards/collection`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ identifiers })
    }))
    if (!res?.ok) {
      console.warn('[SF] batch failed', res?.status)
      return { ok: false, data: [] }
    }
    const json = await res.json()
    return { ok: true, data: json.data || [] }
  } catch (err) {
    console.warn('[SF] batch threw', err?.message || err)
    return { ok: false, data: [] }
  }
}

// Resolve ONE card name through Scryfall's fuzzy `named` endpoint.
//
// `/cards/collection` (fetchScryfallBatch) matches printed card names only, so
// it 404s on a *flavor* name — Universes Beyond prints "Prime Mirelurk Queen"
// on the face of "Hullbreaker Horror", and a decklist typed off the card
// carries that flavor name. `named?fuzzy` resolves those, plus near-misses.
// Returns null when nothing matched or the name was too ambiguous to pick a
// card (Scryfall answers 404 in both cases).
export async function fetchScryfallNamed(name) {
  const query = String(name || '').trim()
  if (!query) return null
  try {
    const res = await runScryfallRequest(() => fetch(
      sfUrl(`${SF_API_ORIGIN}/cards/named?fuzzy=${encodeURIComponent(query)}`),
      { headers: SF_HEADERS }
    ))
    if (!res?.ok) return null
    const json = await res.json()
    return json?.object === 'card' ? json : null
  } catch (err) {
    console.warn('[SF] named lookup threw', err?.message || err)
    return null
  }
}

// Merge a new entry into an existing one, preferring non-empty new values
// but falling back to the existing value when the new field is null/empty.
// Critical for the "Clear Local Metadata" flow: clearScryfallCache() strips
// metadata fields but keeps image_uris/card_faces — a naive {...old, ...new}
// would overwrite intact cached images with a partial set from card_prints
// rows that pre-date the migration.
function mergeSfEntry(existing, next) {
  if (!existing) return next
  const out = { ...existing }
  for (const [key, value] of Object.entries(next)) {
    if (value == null) continue
    if (Array.isArray(value) && value.length === 0) continue
    if (typeof value === 'object' && !Array.isArray(value)) {
      // For image_uris: only take the new object if it carries at least as
      // many populated sizes as the existing one. Otherwise we'd downgrade
      // a 4-size IDB image_uris to a partial 2-size card_prints one.
      if (key === 'image_uris' && existing.image_uris) {
        const existingCount = Object.values(existing.image_uris).filter(Boolean).length
        const nextCount = Object.values(value).filter(Boolean).length
        if (nextCount < existingCount) continue
      }
      if (Object.keys(value).length === 0) continue
    }
    out[key] = value
  }
  return out
}

// Resolve as many missing cards as possible from our own card_prints table
// (one batched Supabase call) before falling back to Scryfall. Returns the
// subset of input cards that still need a Scryfall fetch. Entries that come
// from card_prints are merged into _sfMap and persisted to IDB so future
// loads hit the in-memory cache directly.
async function enrichFromCardPrints(cards, onProgress) {
  if (!cards?.length) return []
  if (!_sfMap) _sfMap = {}

  const scryfallIds = []
  const setColPairs = []
  for (const c of cards) {
    const sid = c.scryfall_id ? String(c.scryfall_id).trim() : null
    if (sid) scryfallIds.push(sid)
    else if (c.set_code && c.collector_number) setColPairs.push({ set_code: c.set_code, collector_number: c.collector_number })
  }

  let rowsByScryfallId = new Map()
  let rowsBySetCol = new Map()
  try {
    if (scryfallIds.length)  rowsByScryfallId = await fetchCardPrintsByScryfallIds(scryfallIds, onProgress)
    if (setColPairs.length)  rowsBySetCol     = await fetchCardPrintsBySetCollector(setColPairs)
  } catch (err) {
    console.warn('[card_prints] enrich query failed, falling back to Scryfall', err?.message || err)
    return cards
  }

  if (!rowsByScryfallId.size && !rowsBySetCol.size) return cards

  const newEntries = []
  const resolved = new Set()
  for (const c of cards) {
    const sid = c.scryfall_id ? String(c.scryfall_id).trim() : null
    let row = sid ? rowsByScryfallId.get(sid) : null
    if (!row && c.set_code && c.collector_number) {
      row = rowsBySetCol.get(`${c.set_code}|${c.collector_number}`) || null
    }
    if (!row) continue
    const entry = cardPrintRowToSfEntry(row)
    if (!entry) continue
    // Reject rows that pre-date the filter-columns migration — they have
    // NULL type_line/rarity and would break the filter bar. Let Scryfall
    // fetch them and the next ensureCardPrints() upsert will backfill.
    if (!entry.type_line) continue
    _sfMap[entry.key] = mergeSfEntry(_sfMap[entry.key], entry)
    newEntries.push(_sfMap[entry.key])
    resolved.add(c)
  }

  if (newEntries.length) {
    try { await putScryfallEntries(newEntries) }
    catch (err) { console.warn('[card_prints] IDB persist failed', err?.message || err) }
    console.log(`[card_prints] resolved ${newEntries.length}/${cards.length} from Supabase`)
  }

  return cards.filter(c => !resolved.has(c))
}

// True when an entry is absent, was stripped by clearScryfallCache, or still
// lacks oracle text. card_prints now supplies oracle_text for ~99.85% of
// printings, so most entries are complete straight after enrichFromCardPrints;
// only the residual (pre-migration rows, or the ~0.15% of prints with no
// oracle_id) has oracle_text == null and falls through to Scryfall. Vanilla
// cards carry oracle_text === '' (non-null), so they don't perpetually refetch.
function entryNeedsScryfall(entry) {
  return !entry || !entry.type_line || entry.oracle_text == null
}

function cardsNeedingScryfall(cards) {
  return cards.filter(c => entryNeedsScryfall(_sfMap[`${c.set_code}-${c.collector_number}`]))
}

export async function enrichCards(cards, onProgress) {
  // Load from IDB if not in memory yet
  if (!_sfMap) {
    const result = await loadCacheFromIDB()
    if (result) {
      _sfMap = result.map
      // If metadata expired, we fall through to fetch fresh metadata below.
      if (!result.pricesExpired) {
        const missing = cardsNeedingScryfall(cards)
        if (missing.length === 0) {
          console.log(`[SF] all ${cards.length} cards cached — skip fetch`)
          onProgress?.(100, '')
          return _sfMap
        }
        // card_prints (Supabase) now supplies oracle_text too, alongside
        // type_line/mana/etc., so this pass completes most cards without any
        // Scryfall call. Re-check who still lacks oracle text (residual /
        // pre-migration rows) and send only those to Scryfall.
        await enrichFromCardPrints(missing, onProgress)
        const stillMissing = cardsNeedingScryfall(missing)
        if (stillMissing.length) {
          console.log(`[SF] ${stillMissing.length}/${missing.length} cards need Scryfall after card_prints`)
          await fetchAndMerge(stillMissing, null)
        }
        return _sfMap
      }
      // Metadata expired — try card_prints first, Scryfall for the rest.
      await enrichFromCardPrints(cards, onProgress)
      const stillMissing = cardsNeedingScryfall(cards)
      if (stillMissing.length) {
        console.log(`[SF] metadata expired, refetching ${stillMissing.length}/${cards.length} via Scryfall`)
        await fetchAndMerge(stillMissing, onProgress)
      } else {
        onProgress?.(100, '')
      }
      return _sfMap
    }
  } else {
    const missing = cardsNeedingScryfall(cards)
    if (missing.length === 0) {
      onProgress?.(100, '')
      return _sfMap
    }
    await enrichFromCardPrints(missing, onProgress)
    const stillMissing = cardsNeedingScryfall(missing)
    if (stillMissing.length) {
      console.log(`[SF] ${stillMissing.length} cards need Scryfall after card_prints`)
      await fetchAndMerge(stillMissing, onProgress)
    } else {
      onProgress?.(100, '')
    }
    return _sfMap
  }

  // Nothing in IDB at all — try card_prints, then Scryfall for the residual.
  await enrichFromCardPrints(cards, onProgress)
  const stillMissing = cardsNeedingScryfall(cards)
  if (stillMissing.length) {
    console.log(`[SF] cold start: ${stillMissing.length}/${cards.length} cards need Scryfall`)
    await fetchAndMerge(stillMissing, onProgress)
  } else {
    onProgress?.(100, '')
  }
  return _sfMap
}

function buildEntryFromScryfall(r) {
  const key = `${r.set}-${r.collector_number}`
  const existing = _sfMap[key] || {}
  return {
    key,
    set_code:         r.set,
    collector_number: r.collector_number,
    name:             r.name,
    set_name:         r.set_name,
    type_line:        r.type_line,
    rarity:           r.rarity,
    prices:           r.prices,
    prices_prev:      existing.prices || null,
    color_identity:   r.color_identity || [],
    cmc:              r.cmc ?? null,
    legalities:       r.legalities || {},
    artist:           r.artist || null,
    // Preserve empty string for vanilla creatures so we can distinguish
    // "fetched, no rules text" from "never fetched" (null/undefined). The
    // missing-check downstream treats null/undefined as needing Scryfall.
    oracle_text:      (r.oracle_text || r.card_faces?.[0]?.oracle_text || '').slice(0, 600),
    power:            r.power ?? null,
    toughness:        r.toughness ?? null,
    produced_mana:    r.produced_mana || null,
    keywords:         r.keywords || [],
    image_uris: existing.image_uris || (r.image_uris ? {
      small:    r.image_uris.small,
      normal:   r.image_uris.normal,
      large:    r.image_uris.large,
      art_crop: r.image_uris.art_crop || null,
    } : null),
    mana_cost: r.mana_cost || r.card_faces?.[0]?.mana_cost || null,
    card_faces: existing.card_faces || r.card_faces?.map(f => ({
      image_uris: f.image_uris ? {
        small:  f.image_uris.small,
        normal: f.image_uris.normal,
        large:  f.image_uris.large,
      } : null,
      name: f.name,
      mana_cost: f.mana_cost || null,
    })) || null,
    attraction_lights: Array.isArray(r.attraction_lights) ? r.attraction_lights : null,
  }
}

async function fetchAndMerge(cards, onProgress) {
  if (!_sfMap) _sfMap = {}
  const total = Math.ceil(cards.length / BATCH_SIZE)
  if (!total) return

  // Build batch tasks up front, then drain with a worker pool.
  const tasks = []
  for (let i = 0; i < cards.length; i += BATCH_SIZE) {
    const batch = cards.slice(i, i + BATCH_SIZE)
    tasks.push(batch.map(c =>
      c.collector_number && c.set_code
        ? { set: c.set_code, collector_number: c.collector_number }
        : { name: c.name }
    ))
  }

  let cursor = 0
  let completed = 0
  let savedCount = 0
  let failedBatches = 0
  const allScryfallResults = []

  async function worker() {
    while (cursor < tasks.length) {
      const idx = cursor++
      const ids = tasks[idx]
      const { ok, data: results } = await fetchScryfallBatch(ids)
      if (!ok) failedBatches++

      const batchEntries = []
      for (const r of results) {
        const entry = buildEntryFromScryfall(r)
        _sfMap[entry.key] = entry
        batchEntries.push(entry)
        allScryfallResults.push(r)
      }

      // Persist this batch immediately so progress survives reload/close.
      if (batchEntries.length) {
        try {
          await putScryfallEntries(batchEntries)
          await setMeta(SCRYFALL_METADATA_UPDATED_AT_KEY, Date.now())
          savedCount += batchEntries.length
        } catch (err) {
          console.warn('[SF IDB] batch persist failed', err)
        }
      }

      completed++
      onProgress?.(
        Math.round((completed / total) * 100),
        `Fetching card data… (${Math.min(completed * BATCH_SIZE, cards.length)} / ${cards.length})`
      )
    }
  }

  const poolSize = Math.min(SF_CONCURRENCY, tasks.length)
  await Promise.all(Array.from({ length: poolSize }, () => worker()))

  if (savedCount) console.log(`[SF IDB] saved ${savedCount} entries (incremental)`)
  if (failedBatches) {
    console.warn(`[SF] ${failedBatches}/${total} batches failed — cards will retry on next load`)
    // Surface as a soft error so callers can show a banner; cards left out of
    // _sfMap will naturally re-attempt on the next enrichCards() call since
    // they remain "missing" in the per-key check.
    throw new Error(`Scryfall fetch incomplete: ${failedBatches} of ${total} batches failed`)
  }

  // Push freshly-fetched data into the shared card_prints table so the next
  // user (or this user on next load) gets it from Supabase instead of Scryfall.
  // Fire-and-forget — errors are non-fatal and already logged inside.
  if (allScryfallResults.length) {
    ensureCardPrints(allScryfallResults).catch(err =>
      console.warn('[card_prints] push-back upsert failed', err?.message || err)
    )
  }
}

// ── Utilities ─────────────────────────────────────────────────────────────────

// ── Price configuration ───────────────────────────────────────────────────────
//
// price_source encodes both marketplace and price type, e.g. 'cardmarket_trend'
// Available sources and what Scryfall field they map to:
//
//   Cardmarket (EUR — official Scryfall/Cardmarket data):
//     cardmarket_trend      → eur / eur_foil        (Cardmarket Trend — default)
//
//   TCGPlayer (USD):
//     tcgplayer_market      → usd / usd_foil         (TCGPlayer Market)
//     tcgplayer_market_etched → usd_etched           (TCGPlayer Etched Foil market)
//
//   MTGO:
//     mtgo_tix              → tix                    (MTGO Ticket price)
//
// Note: Cardmarket Low, Avg, Avg7, Avg30 require the official Cardmarket API
// (OAuth, developer registration). Scryfall only exposes Cardmarket Trend.
// These can be added later via a backend proxy if needed.

export const PRICE_SOURCES = [
  {
    id:          'cardmarket_trend',
    label:       'Cardmarket — Trend',
    description: 'Europe\'s largest MTG marketplace trend price (via Scryfall)',
    currency:    'EUR',
    symbol:      '€',
    field:       'eur',
    foilField:   'eur_foil',
  },
  {
    id:          'tcgplayer_market',
    label:       'TCGPlayer — Market',
    description: 'North American TCGPlayer market price (via Scryfall)',
    currency:    'USD',
    symbol:      '$',
    field:       'usd',
    foilField:   'usd_foil',
  },
]

export function getPriceSource(priceSourceId = 'cardmarket_trend') {
  return PRICE_SOURCES.find(s => s.id === priceSourceId) || PRICE_SOURCES[0]
}

export function getScryfallKey(card) {
  const setCode = String(card?.set_code || '').trim().toLowerCase()
  const collectorNumber = String(card?.collector_number || '').trim()
  return `${setCode}-${collectorNumber}`
}

// ── Manual price overrides (localStorage) ────────────────────────────────────
// Keyed by card DB id (string). Price stored in native source currency.

const MANUAL_PRICES_KEY = 'arcanevault_manual_prices'

export function getManualPrices() {
  try { return JSON.parse(localStorage.getItem(MANUAL_PRICES_KEY) || '{}') } catch { return {} }
}
export function setManualPrice(cardId, price) {
  const map = getManualPrices()
  if (price == null || price === '') delete map[String(cardId)]
  else map[String(cardId)] = parseFloat(price)
  localStorage.setItem(MANUAL_PRICES_KEY, JSON.stringify(map))
}
export function getManualPrice(cardId) {
  if (!cardId) return null
  const v = getManualPrices()[String(cardId)]
  return v != null ? v : null
}

// Returns { value, symbol, suffix, currency, isFallback, pct } in the native price currency.
// Only looks at the user-selected source — no cross-source fallback.
export function getPriceWithMeta(sfCard, foil, { price_source = 'cardmarket_trend' } = {}) {
  if (!sfCard?.prices) return null
  const p    = sfCard.prices
  const prev = sfCard.prices_prev || null
  const source = getPriceSource(price_source)

  const calcPct = (current, field) => {
    if (!prev) return null
    const prevVal = parseFloat(prev[field] || 0)
    if (!prevVal || !current) return null
    return ((current - prevVal) / prevVal) * 100
  }

  const preferredField = foil ? source.foilField : source.field
  const preferred = parseFloat(p[preferredField] || 0)
  if (preferred) return {
    value: preferred,
    symbol: source.symbol,
    suffix: source.suffix || '',
    currency: source.currency || (source.symbol === '€' ? 'EUR' : source.symbol === '$' ? 'USD' : 'TIX'),
    isFallback: false,
    pct: calcPct(preferred, preferredField),
  }

  // Price not available in selected source — return null. User can enter manually.
  return null
}

// Returns numeric price. Checks manual overrides first, then Scryfall data.
export function getPrice(sfCard, foil, { price_source = 'cardmarket_trend', cardId = null } = {}) {
  if (cardId != null) {
    const manual = getManualPrice(cardId)
    if (manual != null) return manual
  }
  return getPriceWithMeta(sfCard, foil, { price_source })?.value ?? null
}

/** Format a price value in its native price source currency. */
export function formatPrice(value, priceSourceId = 'cardmarket_trend') {
  if (value == null) return '—'
  const source = getPriceSource(priceSourceId)
  return `${source.symbol}${value.toFixed(2)}`
}

/** Format a PriceWithMeta object in its native currency. */
export function formatPriceMeta(meta) {
  if (!meta) return '—'
  return `${meta.symbol}${meta.value.toFixed(2)}`
}

// Scryfall CDN image URLs put the size in the path
// (//cards.scryfall.io/<size>/front/.../id.jpg); small/normal/large/art_crop all
// share that path and differ only in the size segment. So we can derive a size
// an entry doesn't store explicitly — e.g. card_prints rows hold only the
// 'normal' URL, so grid thumbnails ('small') are derived from it. No-op for
// non-Scryfall URLs (returns them unchanged).
//
// `png` is the one tier with its own extension; every other tier is .jpg, so the
// extension has to move with the size segment or the derived URL 404s.
export function scryfallImageAtSize(url, size) {
  if (!url || !size) return url || null
  const retiered = url.replace(
    /(\/\/cards\.scryfall\.io\/)(?:small|normal|large|png|art_crop|border_crop)(\/)/,
    `$1${size}$2`,
  )
  if (retiered === url) return url
  return size === 'png'
    ? retiered.replace(/\.jpg(\?|$)/, '.png$1')
    : retiered.replace(/\.png(\?|$)/, '.jpg$1')
}

// Build a Scryfall CDN image URL straight from a card's scryfall id — no cached
// image_uris needed. The CDN lays cards out by id:
//   //cards.scryfall.io/<size>/front/<id[0]>/<id[1]>/<id>.jpg
// `png` is the one tier with a .png extension; every other tier is .jpg. Always
// the front face (the only face an id resolves to on this path). Returns null
// for a missing/invalid id so callers can fall back to a placeholder.
export function scryfallImageUrlFromId(scryfallId, size = 'normal') {
  if (typeof scryfallId !== 'string' || scryfallId.length < 2) return null
  const ext = size === 'png' ? 'png' : 'jpg'
  return `https://cards.scryfall.io/${size}/front/${scryfallId[0]}/${scryfallId[1]}/${scryfallId}.${ext}`
}

// Intrinsic widths of the Scryfall tiers we render cards at.
export const SCRYFALL_TIER_WIDTH = { small: 146, normal: 488, large: 672 }

const TIERS_SMALLEST_FIRST = ['small', 'normal', 'large']

// Pick the tier to render a tile at. The browser scales images to *device*
// pixels, not CSS pixels, so the tier has to follow devicePixelRatio: a 146px
// tile is a 1:1 match for `small` on a plain monitor, but wants 292px on a retina
// laptop and ~440px on a phone — and DeckLoom ships as an Android app, so those
// are normal, not edge cases. Serving `small` there would upscale it into mush.
//
// This used to prefer a tier the tile divides into by a power of two, on the
// theory that the browser could then draw a pre-filtered mip level. Since
// `small` became native-only that preference could no longer change the answer
// downwards — only upwards, to `large` — and the one width where it did (a 168px
// tile taking 672 on an exact 4:1) is the case measured as *worse* than 488-WebP
// off-level. So: smallest covering tier, and WebP wins over mip alignment.
//
// How close a tier's native width must be to the device width to be served at
// that native size rather than rescaled. Also the slack that stops the choice
// being a knife edge: the responsive grid stretches columns to fill a row, so
// `comfortable` paints anywhere from 133 to 161px, and an exact-fit rule flips
// tiers as the window resizes — 1547px wide lands on 150.9 and misses 146 by
// five pixels, 2560px lands on 146.7 and misses by less than one.
const TIER_MATCH_TOLERANCE = 0.06

// Tiers big enough to reduce from. `small` is deliberately absent: Scryfall
// renders it at 146 with sharpening baked in for that size. At its native width
// that sharpening resolves card text a reduction from 488 averages away, but
// rescaled in either direction it degrades into artifacts. A/B'd against 488 on
// both a low-contrast old frame and a modern one, `small` wins at 146–153 and
// loses by 128 (a 1.14:1 reduction) and by 161 (a 1.10x upscale) — so it is
// offered only near native, never as a general-purpose source. Callers that
// want it for an icon-sized thumb, where bytes matter and legibility does not,
// should ask for it explicitly.
const REDUCIBLE_TIERS = ['normal', 'large']

export function pickImageTier(cssWidth, dpr = 1) {
  const deviceWidth = cssWidth * (dpr > 0 ? dpr : 1)
  const nearNative = TIERS_SMALLEST_FIRST.find(
    t => Math.abs(SCRYFALL_TIER_WIDTH[t] / deviceWidth - 1) <= TIER_MATCH_TOLERANCE,
  )
  if (nearNative) return nearNative
  return REDUCIBLE_TIERS.find(t => SCRYFALL_TIER_WIDTH[t] >= deviceWidth) || 'large'
}

// scryfall.com's own grid serves a `grid` tier that the API doesn't list in
// `image_uris`: the same 488x680 picture as `normal`, but WebP instead of JPEG
// and ~40% smaller. WebP has no 8x8 DCT blocking, and those blocks are what beat
// against the sampling grid and make downscaled tiles shimmer — so this is
// visibly cleaner than `normal` at thumbnail sizes, not just lighter.
//
// The tier is undocumented, so callers must fall back to the original URL if it
// 404s. It is WebP-only (`grid/....jpg` 404s), just as `normal` is JPEG-only.
// Returns null when the URL isn't a rewritable Scryfall card image.
export function toScryfallGridWebp(url) {
  if (typeof url !== 'string') return null
  const webp = url.replace(
    /(\/\/cards\.scryfall\.io\/)(?:small|normal|large)(\/.+?)\.jpg/,
    '$1grid$2.webp',
  )
  return webp === url ? null : webp
}

// Resolve the image for a card tile of a given CSS width: the right tier for the
// display's pixel ratio, preferring the WebP where one exists. Returns both URLs
// so the caller can fall back — `grid` is undocumented, so a 404 has to degrade
// to the JPEG rather than leave a broken tile.
//
//   { src, fallback } — render `src`; on error, render `fallback` (null if none).
export function resolveTileImage(url, cssWidth, dpr = 1, forceTier = null) {
  // `forceTier` pins the tier (e.g. the card grids pass 'normal' so every tile
  // renders the WebP `grid` variant at one consistent resolution). Otherwise the
  // tier is chosen adaptively from the tile's device pixels.
  const tier = forceTier || pickImageTier(cssWidth, dpr)
  const tiered = scryfallImageAtSize(url, tier)
  if (!tiered) return { src: null, fallback: null }
  // `grid` is 488px, so it's only an alternative to `normal`. Rewriting a `small`
  // URL to it would quietly serve 4x the pixels the tile asked for.
  const webp = tier === 'normal' ? toScryfallGridWebp(tiered) : null
  return webp ? { src: webp, fallback: tiered } : { src: tiered, fallback: null }
}

function anyStoredImageUrl(imgs) {
  return (imgs && (imgs.normal || imgs.large || imgs.small || imgs.png || imgs.art_crop || imgs.border_crop)) || null
}

export function getImageUri(sfCard, size = 'normal') {
  if (!sfCard) return null
  const imgs = sfCard.image_uris || sfCard.card_faces?.[0]?.image_uris
  if (!imgs) return null
  // Use the stored URL for the requested size, else derive it from whatever
  // size the entry does have (card_prints stores only 'normal').
  return imgs[size] || scryfallImageAtSize(anyStoredImageUrl(imgs), size)
}
